<script setup>
import { computed } from 'vue';

const props = defineProps({
  summary: {
    type: [Object, String],
    default: null,
  },
});

const normalizedSummary = computed(() => {
  if (!props.summary) return {};
  if (typeof props.summary === 'string') return { summary: props.summary };
  return props.summary;
});

const hasSummary = computed(() => !!normalizedSummary.value.summary);
const lastConversationDisplayId = computed(
  () => normalizedSummary.value.last_conversation_display_id
);
const lastConversationRoute = computed(() => ({
  name: 'inbox_conversation',
  params: { conversation_id: lastConversationDisplayId.value },
}));
const hasLastConversationSource = computed(
  () => !!lastConversationDisplayId.value
);

const toList = value => {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value];
};

const stableContext = computed(() =>
  toList(normalizedSummary.value.stable_context)
);
const openFollowUpItems = computed(() =>
  toList(normalizedSummary.value.open_follow_up_items)
);
const riskSignals = computed(() =>
  toList(normalizedSummary.value.risk_signals)
);
const followUp = computed(() => normalizedSummary.value.follow_up || {});
const hasFollowUpDetails = computed(
  () =>
    followUp.value.needed ||
    followUp.value.timing ||
    followUp.value.reason ||
    followUp.value.suggested_message
);
</script>

<template>
  <div
    class="px-4 py-3 text-sm border-b border-solid contact-summary border-slate-50 dark:border-slate-700/50"
  >
    <template v-if="hasSummary">
      <h4 class="m-0 text-xs font-medium text-slate-600 dark:text-slate-300">
        {{ $t('CONTACT_SUMMARY.SUMMARY') }}
      </h4>
      <p
        class="mt-1 m-0 leading-5 break-words text-slate-700 dark:text-slate-200"
      >
        {{ normalizedSummary.summary }}
      </p>

      <div
        v-if="hasLastConversationSource"
        class="flex items-center gap-1 mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400"
      >
        <span>{{ $t('CONTACT_SUMMARY.LAST_SOURCE_CONVERSATION') }}:</span>
        <router-link
          :to="lastConversationRoute"
          target="_blank"
          rel="noopener noreferrer"
          class="font-medium hover:underline text-woot-500 dark:text-woot-500"
        >
          {{ `#${lastConversationDisplayId}` }}
        </router-link>
      </div>

      <div
        v-if="normalizedSummary.recent_resolution"
        class="pt-3 mt-3 border-t border-solid border-slate-50 dark:border-slate-800"
      >
        <h4 class="m-0 text-xs font-medium text-slate-600 dark:text-slate-300">
          {{ $t('CONTACT_SUMMARY.RECENT_RESOLUTION') }}
        </h4>
        <p
          class="mt-1 mb-0 leading-5 break-words text-slate-700 dark:text-slate-200"
        >
          {{ normalizedSummary.recent_resolution }}
        </p>
      </div>

      <div
        v-if="hasFollowUpDetails"
        class="pt-3 mt-3 border-t border-solid border-slate-50 dark:border-slate-800"
      >
        <h4 class="m-0 text-xs font-medium text-slate-600 dark:text-slate-300">
          {{ $t('CONTACT_SUMMARY.FOLLOW_UP') }}
        </h4>
        <p
          v-if="followUp.reason"
          class="mt-1 mb-0 leading-5 break-words text-slate-700 dark:text-slate-200"
        >
          {{ followUp.reason }}
        </p>
        <p
          v-if="followUp.timing"
          class="mt-1 mb-0 text-xs leading-5 break-words text-slate-500 dark:text-slate-400"
        >
          {{ $t('CONTACT_SUMMARY.TIMING') }}: {{ followUp.timing }}
        </p>
        <blockquote
          v-if="followUp.suggested_message"
          class="p-2 mt-2 mb-0 leading-5 break-words border-l-2 border-solid rounded bg-slate-25 dark:bg-slate-800/50 border-woot-500 text-slate-700 dark:text-slate-200"
        >
          {{ followUp.suggested_message }}
        </blockquote>
      </div>

      <div
        v-if="openFollowUpItems.length"
        class="pt-3 mt-3 border-t border-solid border-slate-50 dark:border-slate-800"
      >
        <h4 class="m-0 text-xs font-medium text-slate-600 dark:text-slate-300">
          {{ $t('CONTACT_SUMMARY.OPEN_ITEMS') }}
        </h4>
        <ul class="mt-1 mb-0 pl-4 leading-5 text-slate-700 dark:text-slate-200">
          <li v-for="item in openFollowUpItems" :key="item" class="break-words">
            {{ item }}
          </li>
        </ul>
      </div>

      <div
        v-if="stableContext.length"
        class="pt-3 mt-3 border-t border-solid border-slate-50 dark:border-slate-800"
      >
        <h4 class="m-0 text-xs font-medium text-slate-600 dark:text-slate-300">
          {{ $t('CONTACT_SUMMARY.STABLE_CONTEXT') }}
        </h4>
        <ul class="mt-1 mb-0 pl-4 leading-5 text-slate-700 dark:text-slate-200">
          <li v-for="item in stableContext" :key="item" class="break-words">
            {{ item }}
          </li>
        </ul>
      </div>

      <div
        v-if="riskSignals.length"
        class="pt-3 mt-3 border-t border-solid border-slate-50 dark:border-slate-800"
      >
        <h4 class="m-0 text-xs font-medium text-slate-600 dark:text-slate-300">
          {{ $t('CONTACT_SUMMARY.RISK_SIGNALS') }}
        </h4>
        <ul class="mt-1 mb-0 pl-4 leading-5 text-slate-700 dark:text-slate-200">
          <li v-for="item in riskSignals" :key="item" class="break-words">
            {{ item }}
          </li>
        </ul>
      </div>
    </template>

    <p v-else class="m-0 leading-5 text-slate-500 dark:text-slate-400">
      {{ $t('CONTACT_SUMMARY.EMPTY') }}
    </p>
  </div>
</template>
